/* eslint semi: 1 */
import { inject, nextTick, ref, toRaw } from 'vue';

export function useMediaPreviewEvents () {
  const $utils = inject('$utils');

  // To control the state of the audio player, image preview
  // dialog, and video player dialog. The state will be
  // updated when the respective icons are clicked in the
  // media card, and reset when the dialogs are closed.
  const playAudio = ref({ isPlaying: false, audio: null });
  const updateAudioPlayer = ref({ ref: null, action: null, data: null });
  const imagePreview = ref({ dialog: false, image: null });
  const playVideo = ref({ dialog: false, videopreviews: [] });

  /**
   * Handle audio player events emitted from the audio-player component.
   * Currently only handling the 'ENDED_SONG' action to reset the playAudio state.
   * @param event - the event emitted from the audio-player component.
   */
  function handleAudioPlayerChange (event) {
    if (event.action === 'ENDED_SONG') {
      playAudio.value = { audio: null, isPlaying: false };
    }
  }

  /**
   * Handle Play audio preview in the pop-up.
   * TODO: Currently triggering reactivity by changing the
   * whole audio object, we can optimize this by once the
   * audio-player component has been refactored to composition
   * API.
   * @param audio - the audio object to play
   */
  function handleAudioPlayIconClick (audio) {
    const newAudio = {
      isPlaying: true,
      audio: playAudio.value?.audio
        ? $utils.clone(toRaw(playAudio.value.audio))
        : null
    };

    if (!playAudio.value.audio || (playAudio.value.audio && playAudio.value.audio.resourceId !== audio.resourceId)) {
      // No audio is currently playing or a different audio
      // is playing, so we set the new audio to play.
      newAudio.audio = $utils.clone(toRaw(audio));
    } else {
      // The same audio is clicked, so we just want to play
      // it again (e.g. after it ended) without resetting the
      // audio source.
      updateAudioPlayer.value = {
        ref: $utils.generateUniqueID(),
        action: 'playAudioPreview',
        data: toRaw(playAudio.value.audio)
      };
    }

    playAudio.value = newAudio;
  }

  /**
   * Handle Pause audio preview in the pop-up.
   * @param audio - the audio object to pause
   */
  function handleAudioPauseIconClick () {
    updateAudioPlayer.value = {
      ref: $utils.generateUniqueID(),
      action: 'pauseAudioPreview'
    };

    playAudio.value.isPlaying = false;
  }

  /**
   * Handle Image preview in the pop-up.
   * @param image - the image object to preview
   */
  function handleImagePreviewIconClick (image) {
    resetMediaPreviewEvents();
    imagePreview.value = {
      dialog: !!image,
      image
    };
  }

  /**
   * Handle Play video preview in the pop-up
   * @param {Array} videos - list of video previews
   */
  function handleVideoPlayIconClick (videos) {
    resetMediaPreviewEvents();
    playVideo.value = {
      dialog: videos?.length > 0,
      videopreviews: videos ?? []
    };
  }

  /**
   * The purpose of this function is to relay mouse hover and mouse out events to the thumbnail iframe
   * so that content slides animation can be triggered in the thumbnail iframe.
   * @param {string} mouseEvent - the mouse event type ('mouseover' or 'mouseout')
   * @param {Object} item - target media item to relay the event to the thumbnail iframe
   */
  async function handleRelayHoverToIframe (mouseEvent, item) {
    if (!item || !['svg', 'html'].includes(item?.preview?.type)) return;
    const iframe = document.getElementById(`mediaProjectFile_${item.resourceId}`);
    if (!iframe) return;

    // Small delay to ensure the iframe is ready to receive messages
    await new Promise(resolve => setTimeout(resolve, 300));
    await nextTick(() => iframe.contentWindow?.postMessage({ mouse: mouseEvent }, '*'));
  }

  /**
   * Reset all media preview events to their initial state.
   */
  function resetMediaPreviewEvents () {
    playAudio.value = { isPlaying: false, audio: null };
    updateAudioPlayer.value = { ref: null, action: null, data: null };
    imagePreview.value = { dialog: false, image: null };
    playVideo.value = { dialog: false, videopreviews: [] };
  }

  return {
    // Audio player
    playAudio,
    updateAudioPlayer,
    // Image preview dialog
    imagePreview,
    // Video player dialog
    playVideo,
    handleAudioPlayerChange,
    handleAudioPlayIconClick,
    handleAudioPauseIconClick,
    handleImagePreviewIconClick,
    handleVideoPlayIconClick,
    handleRelayHoverToIframe,
    resetMediaPreviewEvents
  };
}
